import { PropsWithClassName } from "@/types/ui";
import { useDocumentActionsContext } from "@hooks/pages/use-document-actions";
import { useIsMobile } from "@hooks/use-mobile";
import { IconBookmark, IconClockShare } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { LocaleDateTime } from "@components/common/content/locale/locale-date-time";
import { SquidexContentLink } from "@components/common/content/squidex/squidex-content-link";
import { SquidexContentStatusBadge } from "@components/common/content/squidex/squidex-content-status-badge";
import { DocumentBurger } from "@components/document/document-burger";
import { Button } from "@components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover-card";
import { cn } from "@lib/utils";
export const DocumentActionsClient = ({
  className
}: PropsWithClassName) => {
  const isMobile = useIsMobile();
  const tDomain = useTranslations("common");
  const {
    entity: entityState,
    documentMenuItems,
    bookmark,
    headerNode
  } = useDocumentActionsContext().state ?? {};
  const entity = entityState?.entity;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const UpdatedAtBlock = useMemo(() => {
    if (!entity?.updatedAt && !entity?.content?.updatedAt) {
      return null;
    }
    return <div className="font-medium text-muted-foreground">
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button variant="link" size={isMobile ? "icon" : "default"}>
              {isMobile && <IconClockShare />}
              {!isMobile && <LocaleDateTime date={entity?.content?.updatedAt ?? entity.updatedAt} options={{
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            }} />}
            </Button>
          </HoverCardTrigger>
          <HoverCardContent className="w-full">
            <div className="flex justify-between gap-4">
              <div className="space-y-1">
                <h4 className="border-b pb-1 text-sm font-semibold">
                  {tDomain("domain.entity.fields.updatedAt.label")}
                </h4>
                <p className="text-sm">
                  <span className="font-bold">
                    {tDomain("domain.entity.fields.createdAt.description")}: &nbsp;
                  </span>
                  <span>
                    {<LocaleDateTime date={entity?.content?.createdAt ?? entity.createdAt} options={{
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  }} />}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-bold">
                    {tDomain("domain.entity.fields.updatedAt.description")}: &nbsp;
                  </span>
                  <span>
                    {<LocaleDateTime date={entity?.content?.updatedAt ?? entity.updatedAt} options={{
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  }} />}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-bold">
                    {tDomain("domain.entity.fields.updatedBy.description")}: &nbsp;
                  </span>
                  <span>{entity.updatedBy}</span>
                </p>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>;
  }, [entity?.content?.createdAt, entity?.content?.updatedAt, entity?.createdAt, entity?.updatedAt, entity?.updatedBy, isMobile, tDomain]);
  const trailingActions = <>
      {headerNode?.clusterNode}
      {!entityState?.hideCreationDate && UpdatedAtBlock}
      {entity?.content?.status && !entityState?.hideStatusBadge && <SquidexContentStatusBadge status={entity?.content?.status} size={isMobile ? "icon" : "default"} />}
      {!entityState?.hideSquidexLink && <SquidexContentLink content={entity?.content} className="h-7 w-7" />}
      {bookmark && <Button variant="outline" size="icon" className="h-7 w-7">
          <IconBookmark />
        </Button>}
      {headerNode?.node && !headerNode?.showFirst && headerNode.node}
      {documentMenuItems && <DocumentBurger items={documentMenuItems} />}
    </>;
  return <div className={cn("flex flex-wrap items-center gap-2", className, {
    "ml-0": isMobile
  })}>
      {headerNode?.node && headerNode?.showFirst && headerNode.node}
      {headerNode?.clusterNode ? <div className="flex w-max max-w-full shrink-0 flex-wrap items-center gap-2">
          {trailingActions}
        </div> : trailingActions}
    </div>;
};